type NetworkRequestDetectionResult = {
  gtmBlocked: boolean;
  datadogBlocked: boolean;
};

const DATADOG_INTAKE_URL = 'https://browser-intake-datadoghq.eu/api/v2/logs';

export const isGtmBlocked = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (process.env.NEXT_PUBLIC_GTM_DISABLED === 'true') return false;
  if (!process.env.NEXT_PUBLIC_GTM_ID) return false;

  const dataLayer = window.dataLayer as
    | Array<Record<string, unknown>>
    | undefined;
  const hasGtmObject = Boolean(window.google_tag_manager);
  const hasGtmStart =
    dataLayer?.some((entry) => entry['gtm.start'] !== undefined) ?? false;

  return !hasGtmObject || !hasGtmStart;
};

export const isDatadogBlocked = async (): Promise<boolean> => {
  try {
    await fetch(DATADOG_INTAKE_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: '{}',
    });
    return false;
  } catch {
    return true;
  }
};

export const checkNetworkRequests =
  async (): Promise<NetworkRequestDetectionResult> => {
    const gtmBlocked = isGtmBlocked();
    const datadogBlocked = await isDatadogBlocked();
    if (gtmBlocked || datadogBlocked) {
      logNetworkDetectionResult({ gtmBlocked, datadogBlocked }).catch(
        (error: Error) => {
          console.error('Network detection logging failed:', error.message);
        },
      );
    }

    return { gtmBlocked, datadogBlocked };
  };

const logNetworkDetectionResult = async (
  results: NetworkRequestDetectionResult,
): Promise<void> => {
  const workerUrl = process.env.NEXT_PUBLIC_NETWORK_DETECTION_WORKER_URL;
  if (!workerUrl) return;

  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    const uid = auth.currentUser?.uid;

    await fetch(workerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: 'Network request blocking detected',
        meta: {
          usr: {
            id: uid,
          },
          level: 'warn',
          gtmBlocked: results.gtmBlocked,
          datadogBlocked: results.datadogBlocked,
          http: {
            useragent: navigator.userAgent,
          },
        },
      }),
    });
  } catch (error: unknown) {
    console.error(
      'Failed to send log to worker:',
      error instanceof Error ? error.message : error,
    );
  }
};
